import { create } from 'zustand';
import { PathologyLab, MSOPractice, VerticalType } from '../types';
import { getDefaultVertical } from '../utils/verticalConfig';

// Strategy filter IDs that are mutually exclusive
const STRATEGY_FILTERS = ['strategy-platform', 'strategy-addon'];

interface FilterStore {
  // Vertical selection
  vertical: VerticalType;
  setVertical: (vertical: VerticalType) => void;

  // Filter state — pendingFilters are UI selections, activeFilters drive data
  pendingFilters: string[];
  activeFilters: string[];
  toggleFilter: (filterId: string) => void;
  clearFilters: () => void;
  applyAllFilters: () => void;

  // Pipeline data
  pipelineData: (PathologyLab | MSOPractice)[];
  excludedData: (PathologyLab | MSOPractice)[];
  setPipelineData: (data: (PathologyLab | MSOPractice)[]) => void;
  setExcludedData: (data: (PathologyLab | MSOPractice)[]) => void;

  // Animation state
  isAnimating: boolean;
  currentStage: number;
  setAnimating: (isAnimating: boolean) => void;
  setCurrentStage: (stage: number) => void;

  // Sound enabled
  soundEnabled: boolean;
  toggleSound: () => void;

  // Particle field state
  showParticles: boolean;
  isTransitioning: boolean;
  setShowParticles: (show: boolean) => void;
  setTransitioning: (transitioning: boolean) => void;
}

export const useFilterStore = create<FilterStore>((set) => ({
  // Initial state
  vertical: getDefaultVertical(),
  pendingFilters: [],
  activeFilters: [],
  pipelineData: [],
  excludedData: [],
  isAnimating: false,
  currentStage: 0,
  soundEnabled: true,
  showParticles: true,
  isTransitioning: false,

  // Actions
  setVertical: (vertical) => set({ vertical, pendingFilters: [], activeFilters: [], currentStage: 0 }),

  toggleFilter: (filterId) =>
    set((state) => {
      const isRemoving = state.pendingFilters.includes(filterId);
      if (isRemoving) {
        return { pendingFilters: state.pendingFilters.filter((f) => f !== filterId) };
      }

      // If toggling a strategy filter, remove the other strategy filter (mutually exclusive)
      let updated = [...state.pendingFilters];
      if (STRATEGY_FILTERS.includes(filterId)) {
        updated = updated.filter((f) => !STRATEGY_FILTERS.includes(f));
      }
      return { pendingFilters: [...updated, filterId] };
    }),

  clearFilters: () => set({ pendingFilters: [], activeFilters: [], currentStage: 0 }),

  applyAllFilters: () =>
    set((state) => ({
      activeFilters: [...state.pendingFilters],
      isAnimating: true,
    })),

  setPipelineData: (data) => set({ pipelineData: data }),

  setExcludedData: (data) => set({ excludedData: data }),

  setAnimating: (isAnimating) => set({ isAnimating }),

  setCurrentStage: (stage) => set({ currentStage: stage }),

  toggleSound: () => set((state) => ({ soundEnabled: !state.soundEnabled })),

  setShowParticles: (show) => set({ showParticles: show }),

  setTransitioning: (transitioning) => set({ isTransitioning: transitioning }),
}));
